/**
 * Часы сервера на устройстве.
 *
 * Состояние сессии («скоро», «идёт», «завершена») решает сервер по своим часам, а экран раньше
 * сверял границы с часами устройства. Убежавшие вперёд часы превращали идущую сессию в
 * завершённую: блок сессии и комната пропадали с заявки, хотя сервер считал её идущей.
 *
 * Расхождение запоминается по заголовку `Date` ответов шлюза. Запоминается не разница, а точка
 * отсчёта: серверное время в момент ответа и показание монотонных часов браузера
 * (`performance.now`). Их вручную не переведёшь, поэтому человек, двигающий часы на открытой
 * странице, таймеры не ломает: до следующего ответа отсчёт идёт от точки, а не от часов системы.
 */

/** Меньшее расхождение не стоит поправки: заголовок `Date` точен до секунды. */
const IGNORED_SKEW_MS = 5_000;
/** Ответ, шедший дольше, не говорит о времени сервера ничего надёжного. */
const MAX_ROUND_TRIP_MS = 5_000;

type Anchor = { serverMs: number; monotonicMs: number };

/** Пока расхождения нет, «сейчас» — это часы устройства, как у любого человека без сдвига. */
let anchor: Anchor | null = null;

const monotonicNow = () => (typeof performance === 'undefined' ? Date.now() : performance.now());

/** «Сейчас» по часам сервера. */
export const serverNow = () =>
  anchor ? anchor.serverMs + (monotonicNow() - anchor.monotonicMs) : Date.now();

/** Сколько часы сервера впереди часов устройства; для тестов и отладки. */
export const getServerSkewMs = () => Math.round(serverNow() - Date.now());

export const resetServerClock = () => {
  anchor = null;
};

/**
 * Запоминает расхождение по ответу сервера. Не заголовок, мусор или слишком долгий ответ -
 * прежняя точка остаётся.
 */
export const noteServerDate = (
  dateHeader: string | null | undefined,
  requestStartedAt: number,
  responseReceivedAt: number
) => {
  const roundTrip = responseReceivedAt - requestStartedAt;
  const server = Date.parse(dateHeader ?? '');
  if (!Number.isFinite(server) || roundTrip < 0 || roundTrip > MAX_ROUND_TRIP_MS) return;

  // Секунды в заголовке усечены: сервер был где-то в [server, server + 1 с), берём середину.
  // Ответ шёл полпути обратно, поэтому к моменту приёма сервер ушёл вперёд ещё на полпути.
  const serverAtArrival = server + 500 + roundTrip / 2;

  anchor =
    Math.abs(serverAtArrival - responseReceivedAt) < IGNORED_SKEW_MS
      ? null
      : { serverMs: serverAtArrival, monotonicMs: monotonicNow() };
};

const toMs = (value: Date | number | string) => new Date(value).getTime();

/** `isPast` из date-fns по часам сервера. */
export const isServerPast = (value: Date | number | string) => toMs(value) < serverNow();

/** `isFuture` из date-fns по часам сервера. */
export const isServerFuture = (value: Date | number | string) => toMs(value) > serverNow();
